/**
 * feat-303-curriculum-drip-release — the per-Lesson "Skip drip" toggle in
 * LessonMetaPanel's cream meta strip (design/NOTES.md §4, mockup
 * lesson-skip-drip.html P1–P3).
 *
 * - One `<button aria-pressed>`; the label is "Skip drip" in BOTH states (S1).
 *   State is carried by aria-pressed, the prefix glyph and the fill, never by
 *   swapping the words.
 * - Colour is NEUTRAL on purpose: success / preview / gold / attention / coral
 *   already mean live / scheduled / progress / fault / Delete in this strip.
 *   Skip drip is an authoring choice, not a status.
 * - Interactive while the Lesson is Hidden, like the schedule control. Never
 *   `disabled={!unit.active}`.
 * - Saving / failed chips are LessonScheduleControl's, verbatim, including
 *   `animate-pulse-slow` (plain `animate-pulse` compiles to nothing here).
 * - The UI renders the PERSISTED value; it is never flipped optimistically.
 */
import React, { useState } from 'react';
import type { QuizUnit } from '../../../services/quizContentAdminService';
import { SKIP_DRIP_LABEL, SKIP_DRIP_TITLE_OFF, SKIP_DRIP_TITLE_ON } from '../drip/dripCopy';

type Status = 'idle' | 'saving' | 'failed';

const CHIP_BASE = 'inline-flex items-center gap-1.5 rounded-full pl-2.5 pr-1 py-1 whitespace-nowrap';

const OFF_CLASS =
  'text-xs font-bold rounded-full px-2.5 py-1 border border-dashed border-inkMuted/25 text-inkMuted hover:bg-white hover:text-ink focus:outline-none focus:ring-2 focus:ring-coral/40 whitespace-nowrap';
const ON_CLASS =
  'text-xs font-bold rounded-full px-2.5 py-1 bg-white border border-softBorder text-ink hover:bg-cream focus:outline-none focus:ring-2 focus:ring-coral/40 whitespace-nowrap';

const LessonSkipDripToggle: React.FC<{
  unit: Pick<QuizUnit, 'id' | 'drip_bypass'>;
  onSave: (patch: { drip_bypass: boolean }) => Promise<void>;
}> = ({ unit, onSave }) => {
  const [status, setStatus] = useState<Status>('idle');
  const [errorText, setErrorText] = useState('');
  const on = !!unit.drip_bypass;

  // A different Lesson reuses this instance; a stale failure must not follow.
  // Reset during render on an actual id CHANGE (not a mount effect, which can
  // flush after the first click and wipe its saving state).
  const [forId, setForId] = useState(unit.id);
  if (forId !== unit.id) {
    setForId(unit.id);
    setStatus('idle');
    setErrorText('');
  }

  const toggle = async () => {
    setStatus('saving');
    try {
      await onSave({ drip_bypass: !on });
      setStatus('idle');
      setErrorText('');
    } catch (err) {
      setErrorText(err instanceof Error ? err.message : String(err));
      setStatus('failed');
    }
  };

  if (status === 'saving') {
    return (
      <span
        data-testid="lesson-skip-drip"
        aria-busy="true"
        className={`${CHIP_BASE} bg-preview-light text-preview cursor-progress`}
      >
        <span
          className="w-2 h-2 rounded-full border-[1.5px] border-preview animate-pulse-slow"
          aria-hidden="true"
        />
        <span className="text-xs font-bold pr-1">Saving…</span>
      </span>
    );
  }

  if (status === 'failed') {
    return (
      <span
        title={errorText || undefined}
        className={`${CHIP_BASE} bg-danger-surface border border-danger-line text-danger-text`}
      >
        <button type="button" data-testid="lesson-skip-drip" onClick={toggle} className="text-xs font-bold">
          Couldn't save — retry
        </button>
        <span className="text-[13px] leading-none pr-1" aria-hidden="true">
          ↻
        </span>
      </span>
    );
  }

  return (
    <button
      type="button"
      data-testid="lesson-skip-drip"
      aria-pressed={on}
      title={on ? SKIP_DRIP_TITLE_ON : SKIP_DRIP_TITLE_OFF}
      onClick={toggle}
      className={on ? ON_CLASS : OFF_CLASS}
    >
      {/* U+2713 / U+FF0B (the `＋ Schedule` glyph) — decorative, aria-pressed carries state. */}
      <span aria-hidden="true">{on ? '✓ ' : '＋ '}</span>
      {SKIP_DRIP_LABEL}
    </button>
  );
};

export default LessonSkipDripToggle;
